// src/contexts/CellarContext.js

import React, { createContext, useState, useCallback } from "react";
import {
  getFirestore,
  doc,
  collection,
  getDocs,
  getDoc
} from "firebase/firestore";

export const CellarContext = createContext();

export function CellarProvider({ children }) {
  const [beers, setBeers] = useState([]);
  const [totalPanels, setTotalPanels] = useState(1);
  const [processing, setProcessing] = useState(false);

  // This replicates your Swift getCellarEntries(...) logic
  // but in React with Firestore
  const getCellarEntries = useCallback(
    async (userSafeEmail, isPhone = true) => {
      setProcessing(true);
      setBeers([]);
      setTotalPanels(1);

      if (!userSafeEmail) {
        // If user is not logged in, just bail out
        setProcessing(false);
        return;
      }

      try {
        const db = getFirestore();

        // 2) Get all beer documents from the "cellar" subcollection
        const cellarRef = collection(db, "users", userSafeEmail, "cellar");
        const cellarSnapshot = await getDocs(cellarRef);

        // 3) For each beer doc, read "quantity/value" doc, build an array
        const beerInfoDictionary = [];
        const tasks = [];

        cellarSnapshot.forEach((beerDoc) => {
          tasks.push(
            (async () => {
              const beerData = beerDoc.data() || {};

              // Emulate Swift's "CraftBeerEntry(with: $0.data())"
              // Just store the fields you need
              const craftBeerEntry = {
                beerName: beerData.beerName || "",
                beerImage: beerData.beerImage || null,
                beerBrewery: beerData.beerBrewery || "",
                beerStyle: beerData.beerStyle || "",    
                beerABV: beerData.beerABV || 0,
                beerBottleSize: beerData.beerBottleSize || "12oz Can",
              };

              // Now get the sub-doc for quantity
              const quantityDocRef = doc(
                db,
                "users",
                userSafeEmail,
                "cellar",
                beerDoc.id,
                "quantity",
                "value"
              );
              const quantityDocSnap = await getDoc(quantityDocRef);
              const quantityData = quantityDocSnap.data();
              const quantityValue = quantityData?.quantity ?? 1;

              beerInfoDictionary.push({
                craftBeerEntry: craftBeerEntry,
                quantity: quantityValue,
              });
            })()
          );
        });

        // Wait for all sub-queries to finish
        await Promise.all(tasks);

        // 4) Sort by beer name (like Swift)
        beerInfoDictionary.sort((b1, b2) => {
          const name1 = b1.craftBeerEntry.beerName || "";
          const name2 = b2.craftBeerEntry.beerName || "";
          return name1.localeCompare(name2);
        });

        // 5) Compute total panels => 21 items per panel if phone, 28 if not
        const chunkSize = isPhone ? 21 : 28;
        const len = beerInfoDictionary.length;
        const newTotal =
          len === 0 ? 1 : Math.floor(len / chunkSize) + (len % chunkSize === 0 ? 0 : 1);

        // 6) Update state
        setBeers(beerInfoDictionary);
        console.log("Beers:", beerInfoDictionary);
        setTotalPanels(newTotal);
      } catch (error) {
        console.error("Error fetching cellar entries:", error);
      } finally {
        setProcessing(false);
      }
    },
    []
  );

  // You might add addToCellar(...) or removeFromCellar(...) here too

  return (
    <CellarContext.Provider
      value={{
        beers,
        totalPanels,
        processing,
        getCellarEntries,
        // addToCellar,
        // removeFromCellar,
      }}
    >
      {children}
    </CellarContext.Provider>
  );
}
